import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Calculator, Download, FileBarChart2, Plus } from "lucide-react";

import { ApiError } from "../../lib/api";
import { useToast } from "../../components/Toast";
import {
  Button,
  Input,
  Select,
  Textarea,
  TableWrap,
  controlClasses,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import { Modal } from "../../components/Modal";
import { listarTarifas } from "../tarifas/api";
import {
  CONCEPTO_LABELS,
  calcularPreview,
  crearReajuste,
  type DetalleReajusteInput,
  type ReajustePreview,
} from "./api";
import { fetchIndiceIpc, parseMes } from "./ipcApi";

export function CrearReajusteModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const toast = useToast();
  const [tarifaId, setTarifaId] = useState<number | "">("");
  const [ipc0, setIpc0] = useState("");
  const [ipc0Mes, setIpc0Mes] = useState("");
  const [ipcAct, setIpcAct] = useState("");
  const [ipcActMes, setIpcActMes] = useState("");
  const [fechaVigencia, setFechaVigencia] = useState("");
  const [localidades, setLocalidades] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [detalles, setDetalles] = useState<DetalleReajusteInput[]>([]);
  const [preview, setPreview] = useState<ReajustePreview | null>(null);

  const tarifasQ = useQuery({
    queryKey: ["tarifas"],
    queryFn: listarTarifas,
  });

  const traerIpc0Mut = useMutation({
    mutationFn: () => {
      const m = parseMes(ipc0Mes);
      if (!m) throw new Error("Selecciona el mes de IPC₀ primero");
      return fetchIndiceIpc(m.anio, m.mes);
    },
    onSuccess: (resp) => {
      setIpc0(resp.valor);
      toast.success(`IPC₀ ${resp.fecha} = ${resp.valor} (BCCh)`);
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof ApiError ? err.message : "No se pudo traer IPC del BCCh";
      toast.error(msg);
    },
  });

  const traerIpcActMut = useMutation({
    mutationFn: () => {
      const m = parseMes(ipcActMes);
      if (!m) throw new Error("Selecciona el mes del IPC actual primero");
      return fetchIndiceIpc(m.anio, m.mes);
    },
    onSuccess: (resp) => {
      setIpcAct(resp.valor);
      toast.success(`IPC ${resp.fecha} = ${resp.valor} (BCCh)`);
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof ApiError ? err.message : "No se pudo traer IPC del BCCh";
      toast.error(msg);
    },
  });

  const previewMut = useMutation({
    mutationFn: () => {
      if (!tarifaId || !ipc0 || !ipcAct || !ipc0Mes || !ipcActMes) {
        throw new Error("Faltan datos para calcular");
      }
      return calcularPreview({
        tarifa_id: Number(tarifaId),
        ipc_0_valor: ipc0,
        ipc_0_mes: ipc0Mes,
        ipc_actual_valor: ipcAct,
        ipc_actual_mes: ipcActMes,
      });
    },
    onSuccess: (resp) => {
      setPreview(resp);
      // Pre-cargar detalles editables a partir del preview
      setDetalles(
        resp.detalles.map((d) => ({
          concepto: d.concepto,
          subconcepto: d.subconcepto,
          valor_anterior: d.valor_anterior,
          valor_nuevo: d.valor_nuevo,
          referencia_tipo: d.referencia_tipo,
          referencia_id: d.referencia_id,
        })),
      );
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof Error ? err.message : "No se pudo calcular preview";
      toast.error(msg);
    },
  });

  const crearMut = useMutation({
    mutationFn: () => {
      if (
        !tarifaId ||
        !ipc0 ||
        !ipcAct ||
        !ipc0Mes ||
        !ipcActMes ||
        !fechaVigencia ||
        !localidades.trim()
      ) {
        throw new Error("Faltan datos obligatorios");
      }
      if (detalles.length === 0) {
        throw new Error("Calcula el preview antes de crear");
      }
      return crearReajuste({
        tarifa_id: Number(tarifaId),
        ipc_0_valor: ipc0,
        ipc_0_mes: ipc0Mes,
        ipc_actual_valor: ipcAct,
        ipc_actual_mes: ipcActMes,
        fecha_vigencia: fechaVigencia,
        localidades_afectadas: localidades.trim(),
        observaciones: observaciones.trim() || null,
        detalles,
      });
    },
    onSuccess: () => onCreated(),
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : "Error al crear";
      toast.error(msg);
    },
  });

  // Recalcula preview automáticamente cuando los 5 campos están completos
  const preview_inputs_listos = useMemo(
    () => Boolean(tarifaId && ipc0 && ipc0Mes && ipcAct && ipcActMes),
    [tarifaId, ipc0, ipc0Mes, ipcAct, ipcActMes],
  );

  function setDetalleValor(idx: number, valor: string): void {
    setDetalles((prev) => {
      const next = [...prev];
      const actual = next[idx];
      if (!actual) return prev;
      next[idx] = { ...actual, valor_nuevo: valor };
      return next;
    });
  }

  function agregarDetalleManual(): void {
    setDetalles((prev) => [
      ...prev,
      {
        concepto: "Grifo",
        subconcepto: "",
        valor_anterior: "0",
        valor_nuevo: "0",
        referencia_tipo: null,
        referencia_id: null,
      },
    ]);
  }

  function quitarDetalle(idx: number): void {
    setDetalles((prev) => prev.filter((_, i) => i !== idx));
  }

  function setDetalleCampo(
    idx: number,
    campo: keyof DetalleReajusteInput,
    valor: string,
  ): void {
    setDetalles((prev) => {
      const next = [...prev];
      const actual = next[idx];
      if (!actual) return prev;
      next[idx] = { ...actual, [campo]: valor };
      return next;
    });
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios(
    [
      tarifaId, ipc0, ipc0Mes, ipcAct, ipcActMes, fechaVigencia, localidades,
      observaciones, detalles,
    ],
  );
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    crearMut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Nuevo reajuste tarifario"
      icon={FileBarChart2}
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar}>
            Cancelar
          </Button>
          <Button
            onClick={() => crearMut.mutate()}
            disabled={crearMut.isPending || detalles.length === 0}
          >
            {crearMut.isPending
              ? "Creando…"
              : "Crear borrador y emitir informe"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Cuadro 1 */}
        <section>
          <h4 className="mb-2 text-sm font-semibold text-neutral-700">
            Cuadro 1 — Datos generales
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Tarifa"
              required
              value={tarifaId}
              onChange={(e) =>
                setTarifaId(e.target.value ? Number(e.target.value) : "")
              }
            >
              <option value="">— Seleccionar —</option>
              {(tarifasQ.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nombre}
                </option>
              ))}
            </Select>
            <Input
              type="date"
              label="Fecha de vigencia (≥ día 15)"
              required
              value={fechaVigencia}
              onChange={(e) => setFechaVigencia(e.target.value)}
            />
            {/* IPC₀ + botón BCCh: control compuesto, input desnudo con
                controlClasses + Button secondary para la descarga. */}
            <div>
              <label className="mb-1 block text-sm font-medium text-neutral-700">
                IPC₀ (último reajuste)
              </label>
              <div className="flex gap-1">
                <input
                  type="number"
                  step="0.01"
                  value={ipc0}
                  onChange={(e) => setIpc0(e.target.value)}
                  placeholder="ej: 95.8"
                  className={controlClasses}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  icon={Download}
                  onClick={() => traerIpc0Mut.mutate()}
                  disabled={!ipc0Mes || traerIpc0Mut.isPending}
                  title="Traer del Banco Central"
                >
                  BCCh
                </Button>
              </div>
            </div>
            <Input
              type="month"
              label="Mes de IPC₀"
              value={ipc0Mes ? ipc0Mes.slice(0, 7) : ""}
              onChange={(e) =>
                setIpc0Mes(e.target.value ? `${e.target.value}-01` : "")
              }
            />
            <div>
              <label className="mb-1 block text-sm font-medium text-neutral-700">
                IPC actual
              </label>
              <div className="flex gap-1">
                <input
                  type="number"
                  step="0.01"
                  value={ipcAct}
                  onChange={(e) => setIpcAct(e.target.value)}
                  placeholder="ej: 100.1"
                  className={controlClasses}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  icon={Download}
                  onClick={() => traerIpcActMut.mutate()}
                  disabled={!ipcActMes || traerIpcActMut.isPending}
                  title="Traer del Banco Central"
                >
                  BCCh
                </Button>
              </div>
            </div>
            <Input
              type="month"
              label="Mes IPC actual"
              value={ipcActMes ? ipcActMes.slice(0, 7) : ""}
              onChange={(e) =>
                setIpcActMes(e.target.value ? `${e.target.value}-01` : "")
              }
            />
          </div>
          <div className="mt-2">
            <Textarea
              label="Localidades afectadas"
              required
              value={localidades}
              onChange={(e) => setLocalidades(e.target.value)}
              rows={2}
            />
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Button
              variant="secondary"
              icon={Calculator}
              onClick={() => previewMut.mutate()}
              disabled={!preview_inputs_listos || previewMut.isPending}
            >
              Calcular preview
            </Button>
            {preview && (
              <span className="text-sm text-neutral-600">
                Factor IN ={" "}
                <strong>{Number(preview.factor_in).toFixed(4)}</strong> ·
                Variación ={" "}
                <strong
                  className={
                    Number(preview.variacion_pct) > 0
                      ? "text-rose-700"
                      : "text-emerald-700"
                  }
                >
                  {Number(preview.variacion_pct).toFixed(2)}%
                </strong>
              </span>
            )}
          </div>
        </section>

        {/* Cuadro 2 */}
        {detalles.length > 0 && (
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-neutral-700">
                Cuadro 2 — Tarifas reajustadas (sin IVA, sin decimales)
              </h4>
              <Button
                variant="link"
                size="sm"
                icon={Plus}
                onClick={agregarDetalleManual}
              >
                agregar concepto manual
              </Button>
            </div>
            <TableWrap className="rounded">
              <table className="min-w-full divide-y divide-neutral-200 text-xs">
                <thead className="bg-neutral-50 text-neutral-500">
                  <tr>
                    <th scope="col" className="px-2 py-1 text-left">Concepto</th>
                    <th scope="col" className="px-2 py-1 text-left">Detalle</th>
                    <th scope="col" className="px-2 py-1 text-right">Anterior</th>
                    <th scope="col" className="px-2 py-1 text-right">Nuevo (editable)</th>
                    <th scope="col" className="px-2 py-1"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 bg-white">
                  {detalles.map((d, i) => (
                    <tr key={i}>
                      <td className="px-2 py-1">
                        {d.referencia_tipo ? (
                          CONCEPTO_LABELS[d.concepto]
                        ) : (
                          <select
                            value={d.concepto}
                            onChange={(e) =>
                              setDetalleCampo(i, "concepto", e.target.value)
                            }
                            className="rounded border border-neutral-450 px-1 text-xs"
                          >
                            <option value="Grifo">Grifo</option>
                            <option value="Corte">Corte</option>
                            <option value="Reposicion">Reposición</option>
                            <option value="RevisionProyecto">
                              Revisión proyecto
                            </option>
                          </select>
                        )}
                      </td>
                      <td className="px-2 py-1">
                        {d.referencia_tipo ? (
                          d.subconcepto || "—"
                        ) : (
                          <input
                            value={d.subconcepto || ""}
                            onChange={(e) =>
                              setDetalleCampo(
                                i,
                                "subconcepto",
                                e.target.value,
                              )
                            }
                            className="w-32 rounded border border-neutral-450 px-1 text-xs"
                          />
                        )}
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums">
                        {d.referencia_tipo ? (
                          `$${Number(d.valor_anterior).toLocaleString("es-CL")}`
                        ) : (
                          <input
                            type="number"
                            value={d.valor_anterior}
                            onChange={(e) =>
                              setDetalleCampo(
                                i,
                                "valor_anterior",
                                e.target.value,
                              )
                            }
                            className="w-24 rounded border border-neutral-450 px-1 text-right text-xs"
                          />
                        )}
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums">
                        <input
                          type="number"
                          value={d.valor_nuevo}
                          onChange={(e) =>
                            setDetalleValor(i, e.target.value)
                          }
                          className="w-24 rounded border border-neutral-450 px-1 text-right text-xs"
                        />
                      </td>
                      <td className="px-2 py-1 text-right">
                        {!d.referencia_tipo && (
                          <Button
                            variant="linkDanger"
                            size="sm"
                            onClick={() => quitarDetalle(i)}
                          >
                            quitar
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
            <p className="mt-1 text-xs text-neutral-500">
              Los valores calculados pueden editarse manualmente — el sistema
              marca el detalle como "editado" para auditoría.
            </p>
          </section>
        )}

        <Textarea
          label="Observaciones"
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          rows={2}
        />
      </div>
      {dialogoDescarte}
    </Modal>
  );
}
